import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

const VehicleDetails = ({ formData, updateFormData, nextStep, prevStep }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Initialize vehicles array based on selected vehicle types if empty
  useEffect(() => {
    if (formData.vehicles.length === 0 && formData.vehicleTypes.length > 0) {
      const initialVehicles = formData.vehicleTypes.map(type => ({
        id: uuidv4(),
        type,
        registrationNumber: '',
        make: '',
        model: '',
        color: '',
        ownerName: formData.residentName,
        isPrimaryVehicle: false
      }));
      
      updateFormData({ vehicles: initialVehicles });
    }
  }, [formData.vehicleTypes, formData.vehicles.length, formData.residentName, updateFormData]);
  
  // Handle change for a specific vehicle field
  const handleVehicleChange = (vehicleId, field, value) => {
    const updatedVehicles = formData.vehicles.map(vehicle => {
      if (vehicle.id === vehicleId) {
        return { ...vehicle, [field]: value };
      }
      return vehicle;
    });
    
    updateFormData({ vehicles: updatedVehicles });
  };
  
  // Handle checkbox change for primary vehicle
  const handlePrimaryVehicleChange = (vehicleId) => {
    const updatedVehicles = formData.vehicles.map(vehicle => ({
      ...vehicle,
      isPrimaryVehicle: vehicle.id === vehicleId
    }));
    
    updateFormData({ vehicles: updatedVehicles });
  };
  
  // Add a new vehicle of a specific type
  const addVehicle = (type) => {
    const newVehicle = {
      id: uuidv4(),
      type,
      registrationNumber: '',
      make: '',
      model: '',
      color: '',
      ownerName: formData.residentName,
      isPrimaryVehicle: false
    };
    
    updateFormData({ vehicles: [...formData.vehicles, newVehicle] });
  };
  
  // Remove a vehicle
  const removeVehicle = (vehicleId) => {
    const updatedVehicles = formData.vehicles.filter(vehicle => vehicle.id !== vehicleId);
    updateFormData({ vehicles: updatedVehicles });
  };
  
  // State for validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Handle form submission with improved validation
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Reset validation errors
    setValidationErrors({});
    const errors = {};
    
    // Basic validation
    if (formData.vehicles.length === 0) {
      errors.general = 'Please add at least one vehicle.';
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }
    
    // Check each vehicle for validation errors
    formData.vehicles.forEach((vehicle, index) => {
      if (!vehicle.registrationNumber || !vehicle.make || !vehicle.model || !vehicle.color || !vehicle.ownerName) {
        errors[vehicle.id] = errors[vehicle.id] || {};
        errors[vehicle.id].fields = 'All required fields must be filled';
      }
      
      // Check registration number format (e.g., MH02AB1234)
      if (vehicle.registrationNumber && !/^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/.test(vehicle.registrationNumber.replace(/\s/g, ''))) {
        errors[vehicle.id] = errors[vehicle.id] || {};
        errors[vehicle.id].regNumber = 'Invalid registration number format (e.g., MH02AB1234)';
      }
    });
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }
    
    // Simulate API call with a slight delay
    setTimeout(() => {
      setIsSubmitting(false);
      // Show success animation
      setIsSuccess(true);
      
      // Redirect to home page after showing success animation
      setTimeout(() => {
        navigate('/');
      }, 1500); // Wait for animation to complete before redirecting
    }, 800);
  };
  
  // Helper function to check if a vehicle has errors
  const hasVehicleErrors = (vehicleId) => {
    return validationErrors[vehicleId] !== undefined;
  };
  
  // Helper function to get specific error message
  const getErrorMessage = (vehicleId, field) => {
    if (validationErrors[vehicleId] && validationErrors[vehicleId][field]) {
      return validationErrors[vehicleId][field];
    }
    return null;
  };
  
  // Group vehicles by type
  const vehiclesByType = {};
  formData.vehicleTypes.forEach(type => {
    vehiclesByType[type] = formData.vehicles.filter(vehicle => vehicle.type === type);
  });
  
  return (
    <div className="w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 relative">
      {/* Success animation overlay */}
      {isSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl p-8 shadow-2xl transform transition-all duration-500 scale-100 opacity-100 flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-green-500 animate-[bounce_1s_infinite]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Success!</h3>
            <p className="text-gray-600 text-center">Your vehicle details have been submitted successfully.</p>
          </div>
        </div>
      )}
      <div className="transform transition-all duration-500 opacity-100 translate-y-0">

        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-800 border-b pb-3">
          Vehicle Details
        </h2>
        <div className="bg-blue-50 p-4 rounded-lg mb-6 shadow-sm border border-blue-100">
          <p className="text-blue-800 font-medium flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            Block {formData.block}, Flat {formData.flatNumber}
          </p>
        </div>
        
        {validationErrors.general && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 border border-red-200 transform transition-all duration-300">
            {validationErrors.general}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {formData.vehicleTypes.map(type => (
            <div 
              key={type} 
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md"
            >
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 sm:p-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  {type === 'Car' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  )}
                  {type === 'Bike' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                  {type}
                </h3>
                <button
                  type="button"
                  onClick={() => addVehicle(type)}
                  className="bg-white text-blue-600 hover:bg-blue-50 hover:scale-105 active:scale-95 font-medium py-1.5 sm:py-2 px-3 sm:px-4 rounded-full shadow-sm flex items-center transition-all duration-200 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add {type}
                </button>
              </div>
              
              <div className="p-4">
                <div className="transition-all duration-300">
                  {vehiclesByType[type].length > 0 ? (
                    <div className="space-y-6">
                      {vehiclesByType[type].map((vehicle, index) => (
                        <div 
                          key={vehicle.id} 
                          className={`bg-gray-50 rounded-lg border ${hasVehicleErrors(vehicle.id) ? 'border-red-300' : 'border-gray-200'} relative overflow-hidden transition-all duration-300 transform hover:shadow-md opacity-100`}
                        >
                          <div className="p-3 sm:p-5">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-medium text-lg text-gray-800 flex items-center">
                                {type} #{index + 1}
                                {vehicle.isPrimaryVehicle && (
                                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Primary</span>
                                )}
                              </h4>
                              <button
                                type="button"
                                onClick={() => removeVehicle(vehicle.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all duration-200 hover:scale-110 active:scale-90"
                                aria-label="Remove vehicle"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                            
                            {hasVehicleErrors(vehicle.id) && (
                              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700">
                                {getErrorMessage(vehicle.id, 'fields') && <p>{getErrorMessage(vehicle.id, 'fields')}</p>}
                                {getErrorMessage(vehicle.id, 'regNumber') && <p>{getErrorMessage(vehicle.id, 'regNumber')}</p>}
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-3 sm:gap-y-4">
                              <div className="relative">
                                <label className="block text-gray-700 text-sm font-medium mb-1">
                                  Registration Number*
                                </label>
                                <input
                                  type="text"
                                  value={vehicle.registrationNumber}
                                  onChange={(e) => handleVehicleChange(vehicle.id, 'registrationNumber', e.target.value)}
                                  className={`shadow-sm border ${getErrorMessage(vehicle.id, 'regNumber') ? 'border-red-300' : 'border-gray-300'} rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                                  placeholder="e.g., MH02AB1234"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">
                                  Make*
                                </label>
                                <input
                                  type="text"
                                  value={vehicle.make}
                                  onChange={(e) => handleVehicleChange(vehicle.id, 'make', e.target.value)}
                                  className="shadow-sm border border-gray-300 rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                  placeholder="e.g., Honda, Toyota"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">
                                  Model*
                                </label>
                                <input
                                  type="text"
                                  value={vehicle.model}
                                  onChange={(e) => handleVehicleChange(vehicle.id, 'model', e.target.value)}
                                  className="shadow-sm border border-gray-300 rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                  placeholder="e.g., Civic, Corolla"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">
                                  Color*
                                </label>
                                <input
                                  type="text"
                                  value={vehicle.color}
                                  onChange={(e) => handleVehicleChange(vehicle.id, 'color', e.target.value)}
                                  className="shadow-sm border border-gray-300 rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                  placeholder="e.g., White, Black"
                                  required
                                />
                              </div>
                              
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">
                                  Owner Name*
                                </label>
                                <input
                                  type="text"
                                  value={vehicle.ownerName}
                                  onChange={(e) => handleVehicleChange(vehicle.id, 'ownerName', e.target.value)}
                                  className="shadow-sm border border-gray-300 rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                  required
                                />
                              </div>
                              
                              <div className="col-span-2 mt-2">
                                <label className="inline-flex items-center cursor-pointer group">
                                  <div className="relative">
                                    <input
                                      type="checkbox"
                                      checked={vehicle.isPrimaryVehicle}
                                      onChange={() => handlePrimaryVehicleChange(vehicle.id)}
                                      className="sr-only"
                                    />
                                    <div className={`w-10 h-5 bg-gray-200 rounded-full shadow-inner transition-colors duration-300 ${vehicle.isPrimaryVehicle ? 'bg-green-500' : ''}`}></div>
                                    <div className={`absolute w-7 h-7 bg-white rounded-full shadow -left-1 -top-1 transition-transform duration-300 transform ${vehicle.isPrimaryVehicle ? 'translate-x-5' : ''} flex items-center justify-center`}>
                                      {vehicle.isPrimaryVehicle && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                  <span className="ml-3 text-gray-700 group-hover:text-gray-900">This is my primary vehicle</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div 
                      className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center transition-opacity duration-300 opacity-100"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v16m8-8H4" />
                      </svg>
                      <p className="text-gray-500">No {type.toLowerCase()} added yet</p>
                      <button 
                        type="button"
                        onClick={() => addVehicle(type)}
                        className="mt-4 text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Add your first {type.toLowerCase()}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={prevStep}
              className="order-2 sm:order-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 flex items-center justify-center hover:scale-[1.02] active:scale-[0.98]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back
            </button>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className={`order-1 sm:order-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 flex items-center justify-center hover:scale-[1.02] active:scale-[0.98] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Next
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleDetails;
